;(function (angular, _) {
  'use strict'

  angular
    .module('kilo.common.directives')
    .directive('pennylaneSettings', PennylaneSettingsDirective)
    .directive('pennylaneSiren', PennylaneSirenDirective)

  /**
   * Clé de contrôle SIREN (algorithme de Luhn) — copie exacte du validateur
   * backend `is-siren.validator.ts` : on double un chiffre sur deux en
   * partant de la droite. Aligne la validation client sur le backend pour
   * éviter un 400 sur une simple faute de frappe.
   */
  function isValidSirenChecksum (value) {
    if (!/^\d{9}$/.test(value)) return false
    let sum = 0
    for (let i = 0; i < value.length; i++) {
      // Position depuis la droite (0 = chiffre de poids faible).
      const positionFromRight = value.length - 1 - i
      let digit = parseInt(value.charAt(i), 10)
      if (positionFromRight % 2 === 1) {
        digit *= 2
        if (digit > 9) digit -= 9
      }
      sum += digit
    }
    return sum % 10 === 0
  }

  PennylaneSettingsController.$inject = [
    // Angular
    '$q',
    '$scope',
    '$window',
    // Libs
    // Managers
    // Services
    'PennylaneService',
    'DisplayService',
    'SnackService',
    'PreferencesService'
    // Const
    // Resolved
  ]

  /**
   * Onglet Préférences « PENNYLANE » — onboarding facture électronique.
   * Saisie de la fiche entreprise + dirigeant (enregistrée sur backend-pennylane),
   * provisioning chez Pennylane, puis connexion OAuth (redirection vers
   * Pennylane, retour sur le state `pennylane-callback`) et suivi de
   * l'activation Plateforme Agréée (pa-registrations).
   */
  function PennylaneSettingsController (
    // Angular
    $q,
    $scope,
    $window,
    // Libs
    // Managers
    // Services
    PennylaneService,
    DisplayService,
    SnackService,
    PreferencesService
    // Const
    // Resolved
  ) {
    const self = this

    // Valeurs EXACTES de l'enum NumberOfEmployees du backend (API Pennylane).
    const EMPLOYEE_RANGES = [
      { value: 'individual', label: 'Indépendant (sans salarié)' },
      { value: '1_5', label: '1 à 5 salariés' },
      { value: '6_15', label: '6 à 15 salariés' },
      { value: '16_25', label: '16 à 25 salariés' },
      { value: '26_50', label: '26 à 50 salariés' },
      { value: '51_100', label: '51 à 100 salariés' },
      { value: '101_150', label: '101 à 150 salariés' },
      { value: 'more_than_150', label: 'Plus de 150 salariés' }
    ]

    // Valeurs EXACTES de l'enum CountryAlpha2 du backend (France + DOM-TOM Pennylane).
    const COUNTRIES = [
      { value: 'FR', label: 'France métropolitaine' },
      { value: 'GP', label: 'Guadeloupe' },
      { value: 'MQ', label: 'Martinique' },
      { value: 'GF', label: 'Guyane' },
      { value: 'RE', label: 'La Réunion' },
      { value: 'YT', label: 'Mayotte' },
      { value: 'PM', label: 'Saint-Pierre-et-Miquelon' },
      { value: 'BL', label: 'Saint-Barthélemy' },
      { value: 'MF', label: 'Saint-Martin' },
      { value: 'WF', label: 'Wallis-et-Futuna' },
      { value: 'PF', label: 'Polynésie française' },
      { value: 'NC', label: 'Nouvelle-Calédonie' },
      { value: 'TF', label: 'Terres australes françaises' },
      { value: 'CP', label: 'Clipperton' }
    ]

    const PROVISIONING_LABELS = {
      pending: 'Non validé',
      provisioned: 'Valider sur Pennylane',
      failed: 'Échec du provisioning'
    }

    // Valeurs EXACTES de l'enum PaSituation du backend (parcours « compte
    // Pennylane existant ») — libellés FR des sous-choix radio de l'état A.
    const PA_SITUATIONS = [
      { value: 'no_pa', label: 'Je n\'ai pas encore désigné ma Plateforme Agréée' },
      { value: 'pennylane', label: 'Pennylane est déjà ma Plateforme Agréée' },
      { value: 'other_pa', label: 'J\'ai une autre Plateforme Agréée désignée' }
    ]

    const CONNECTION_LABELS = {
      disconnected: 'Non connectée',
      connected: 'Connectée',
      error: 'Erreur de connexion'
    }

    // Statuts d'enregistrement PA (pa_registrations Pennylane) — libellés FR ;
    // statut inconnu (ex. draft/archived) affiché brut.
    const PA_STATUS_LABELS = {
      provisioned: 'Désignation PA à signer',
      pending: 'En validation annuaire',
      activated: 'Activée'
    }

    const PA_DIRECTION_LABELS = {
      emission: 'Émission',
      reception: 'Réception',
      emission_and_reception: 'Émission et réception'
    }

    self.$onInit = () => {
      _.extend(self, {
        // Public attributes
        loading: true,
        loadError: false,
        saving: false,
        provisioning: false,
        connecting: false,
        disconnecting: false,
        company: null, // document Company backend-pennylane (null si non créée)
        user: null, // document User (dirigeant) backend-pennylane (null si non créé)
        kiloCompanyInfo: null, // infos entreprise Kilo (préférences), suggestion en état A
        prefilledFromKilo: false, // vrai ssi le bloc Entreprise a été prérempli depuis Kilo
        // Question d'aiguillage état A « Avez-vous déjà un compte Pennylane ? » :
        // null = pas encore répondu (formulaire masqué), false = parcours
        // historique (provisioning), true = parcours « compte existant ».
        hasExistingAccount: null,
        // Sous-choix du parcours « compte existant » (persisté côté backend
        // sous pennylane.paSituation) — null tant que non choisi.
        paSituation: null,
        // Mode édition de la fiche depuis le récapitulatif (parcours « compte
        // existant » uniquement) : vrai = le formulaire réduit remplace le
        // récap. Sortie par Save (update) ou Annuler (restauration).
        editingCompany: false,
        paSituations: PA_SITUATIONS,
        companyData: {},
        userData: {},
        employeeRanges: EMPLOYEE_RANGES,
        countries: COUNTRIES,
        // Suivi PA (pa-registrations) — chargé quand la company est connectée.
        paLoading: false,
        paError: false,
        paRegistrations: null, // null = pas encore chargé ; [] = aucun enregistrement
        emissionAllowed: false,
        // Public methods
        reload,
        save,
        provision,
        connect,
        disconnectPennylane,
        loadPaRegistrations,
        isProvisioned,
        isExistingAccountFlow,
        canConnect,
        canEditCompany,
        startEditingCompany,
        cancelEditingCompany,
        showForm,
        isConnected,
        hasSavedSiren,
        hasActivatedRegistration,
        hasOtherPaSituation,
        hasPennylanePaSituation,
        pdpActivationUrl,
        provisioningStatusLabel,
        connectionStatusLabel,
        connectionStatus,
        paSituationLabel,
        paStatusLabel,
        paDirectionLabel,
        paSiretLabel
      })
      // Retour depuis Pennylane via « Précédent » : le navigateur peut
      // restaurer la page depuis le bfcache avec `connecting` encore vrai
      // (posé avant la redirection) → on réarme le bouton de connexion.
      $window.addEventListener('pageshow', onPageShow)
      $scope.$on('$destroy', () => {
        $window.removeEventListener('pageshow', onPageShow)
      })
      reload()
    }

    /** Événement hors Angular : digest explicite via $applyAsync. */
    function onPageShow (event) {
      if (!event || !event.persisted || !self.connecting) return
      $scope.$applyAsync(() => {
        self.connecting = false
      })
    }

    /**
     * GET /users en tolérant UNIQUEMENT le 404 (aucun dirigeant créé = état
     * légitime, toast déjà supprimé par ignoreErrors côté service). Toute
     * autre erreur (500, réseau…) est propagée : sinon un incident serveur
     * afficherait à tort un formulaire dirigeant vide.
     */
    function getUserOrNull () {
      return PennylaneService.getUsers().catch((err) => {
        if (err && err.status === 404) return null
        throw err
      })
    }

    /**
     * Charge les infos entreprise Kilo (préférences générales) en best-effort :
     * elles servent de SUGGESTION pour préremplir le bloc Entreprise en état A.
     * Tout échec renvoie null — jamais de blocage du formulaire Pennylane.
     */
    function getKiloCompanyInfoOrNull () {
      return PreferencesService.getCompanyPrefs()
        .then((prefs) => (prefs && prefs.companyInfo) || null)
        .catch(() => null)
    }

    /**
     * Charge en parallèle la company et le dirigeant du tenant courant, puis
     * pré-remplit les formulaires.
     */
    function reload () {
      self.loading = true
      self.loadError = false
      // Rechargement complet = abandon d'une éventuelle édition en cours
      // (les formulaires sont re-remplis depuis le backend par fillForms).
      self.editingCompany = false
      return $q.all({
        companies: PennylaneService.getCompanies(),
        user: getUserOrNull(),
        kiloInfo: getKiloCompanyInfoOrNull()
      }).then((res) => {
        // Relation 1:1 par tenant : au plus une company (et un dirigeant).
        self.company = (res.companies && res.companies.data && res.companies.data[0]) || null
        self.user = (res.user && res.user.data) || null
        // Suggestion Kilo pour le préremplissage en état A (avant fillForms).
        self.kiloCompanyInfo = res.kiloInfo || null
        fillForms()
        // Company connectée → charger le suivi PA (best-effort, non bloquant).
        if (isConnected()) loadPaRegistrations()
      }).catch(() => {
        // Erreur réseau/serveur : le toast global (HttpErrorInterceptor) l'affiche déjà.
        self.loadError = true
      }).finally(() => {
        self.loading = false
      })
    }

    /**
     * Recharge les documents backend SANS toucher aux modèles de formulaire :
     * utilisé après un échec (potentiellement partiel) de save() pour que le
     * retry fasse un update — et non un create — tout en conservant la saisie
     * de l'opérateur. Best-effort : en cas de nouvel échec on garde l'état
     * courant (le prochain save/reload resynchronisera).
     */
    function resyncDocuments () {
      return $q.all({
        companies: PennylaneService.getCompanies(),
        user: getUserOrNull()
      }).then((res) => {
        self.company = (res.companies && res.companies.data && res.companies.data[0]) || null
        self.user = (res.user && res.user.data) || null
      }).catch(angular.noop)
    }

    /**
     * Extrait un SIREN (9 chiffres) depuis un SIRET Kilo : ne renvoie la valeur
     * que si la clé de Luhn est valide, sinon '' (on ne suggère jamais un SIREN
     * douteux qui bloquerait le provisioning). Toute saisie non numérique est ignorée.
     */
    function sirenFromSiret (siret) {
      if (!siret) return ''
      const digits = String(siret).replace(/\D/g, '')
      if (digits.length < 9) return ''
      const siren = digits.slice(0, 9)
      return isValidSirenChecksum(siren) ? siren : ''
    }

    /**
     * Normalise un pays Kilo (texte libre) vers un code enum Pennylane
     * (CountryAlpha2). Défaut 'FR' si vide/inconnu — on ne suggère qu'un code
     * valide, l'opérateur ajuste au besoin.
     */
    function normalizeCountry (text) {
      if (!text) return 'FR'
      // Trim + suppression des accents + minuscules pour un matching tolérant.
      const key = String(text).trim().toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const byName = {
        'fr': 'FR',
        'france': 'FR',
        'guadeloupe': 'GP',
        'martinique': 'MQ',
        'guyane': 'GF',
        'la reunion': 'RE',
        'reunion': 'RE',
        'mayotte': 'YT'
      }
      if (byName[key]) return byName[key]
      // Déjà un code à 2 lettres connu (ex. 'FR', 'GP') → conservé tel quel.
      const upper = String(text).trim().toUpperCase()
      if (upper.length === 2 && COUNTRIES.some((c) => c.value === upper)) return upper
      return 'FR'
    }

    /**
     * Pré-remplit les modèles de formulaire.
     * - company existante → valeurs backend (comportement historique inchangé).
     * - état A (company null) → SUGGESTION du bloc Entreprise depuis les infos
     *   Kilo (non sauvegardée). Le bloc dirigeant reste TOUJOURS vide.
     */
    function fillForms () {
      const user = self.user || {}
      self.prefilledFromKilo = false

      if (self.company) {
        // Company existante : on reflète exactement les valeurs backend.
        const company = self.company
        // Situation « compte Pennylane existant » persistée : elle fait foi
        // (null pour une fiche historique sans paSituation).
        self.paSituation = (company.pennylane && company.pennylane.paSituation) || null
        self.companyData = {
          name: company.name || '',
          // 'pending' = SIREN non renseigné côté backend → champ vide à l'écran.
          regNo: (company.regNo && company.regNo !== 'pending') ? company.regNo : '',
          countryAlpha2: company.countryAlpha2 || 'FR',
          numberOfEmployees: company.numberOfEmployees || null,
          activityCode: company.activityCode || '',
          address: company.address || '',
          city: company.city || '',
          postalCode: company.postalCode || ''
        }
      } else {
        // État A : suggestion depuis Kilo (si dispo), sinon tout vide.
        const kilo = self.kiloCompanyInfo || {}
        const kiloAddress = kilo.address || {}
        const address = [kiloAddress.addressLine1, kiloAddress.addressLine2].filter(Boolean).join(', ')
        self.companyData = {
          name: kilo.name || '',
          regNo: sirenFromSiret(kilo.siret),
          countryAlpha2: normalizeCountry(kiloAddress.country),
          numberOfEmployees: null, // absent de Kilo
          activityCode: kilo.apeCode || '',
          address: address,
          city: kiloAddress.city || '',
          postalCode: kiloAddress.postalCode || ''
        }
        // Encart d'info affiché seulement si un champ a réellement été prérempli.
        if (self.kiloCompanyInfo) {
          self.prefilledFromKilo = !!(
            self.companyData.name ||
            self.companyData.regNo ||
            self.companyData.activityCode ||
            self.companyData.address ||
            self.companyData.city ||
            self.companyData.postalCode
          )
        }
      }

      // Bloc dirigeant : jamais prérempli depuis Kilo (décision utilisateur).
      self.userData = {
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || ''
      }
    }

    /**
     * Payload company limité aux champs des DTO backend (le ValidationPipe
     * whitelist rejette tout champ inconnu). regNo omis si vide : le backend
     * le laisse/le garde à 'pending' (et le DTO update interdit 'pending').
     */
    function buildCompanyPayload () {
      const payload = {
        name: self.companyData.name,
        countryAlpha2: self.companyData.countryAlpha2,
        numberOfEmployees: self.companyData.numberOfEmployees,
        activityCode: self.companyData.activityCode,
        address: self.companyData.address,
        city: self.companyData.city,
        postalCode: self.companyData.postalCode
      }
      if (self.companyData.regNo) payload.regNo = self.companyData.regNo
      // Parcours « compte Pennylane existant » : la situation PA choisie est
      // persistée top-level (exposée en retour sous pennylane.paSituation).
      // Jamais envoyée sur le parcours historique (payload inchangé).
      if (isExistingAccountFlow() && self.paSituation) payload.paSituation = self.paSituation
      return payload
    }

    /** Payload dirigeant limité aux champs des DTO backend. */
    function buildUserPayload () {
      return {
        email: self.userData.email,
        firstName: self.userData.firstName,
        lastName: self.userData.lastName,
        phoneNumber: self.userData.phoneNumber
      }
    }

    /**
     * Enregistre la fiche localement (backend-pennylane) : création si rien
     * n'existe, PATCH sinon. AUCUN provisioning Pennylane ici.
     * Parcours « compte Pennylane existant » : fiche RÉDUITE au bloc
     * Entreprise → aucun dirigeant enregistré (pas de provisioning ensuite).
     */
    function save () {
      if (self.saving) return
      self.saving = true
      const userPromise = isExistingAccountFlow()
        ? $q.resolve(null)
        : (self.user
          ? PennylaneService.updateUser(self.user._id, buildUserPayload())
          : PennylaneService.createUser(buildUserPayload()))
      const companyPromise = self.company
        ? PennylaneService.updateCompany(self.company._id, buildCompanyPayload())
        : PennylaneService.createCompany(buildCompanyPayload())
      $q.all([userPromise, companyPromise]).then((res) => {
        if (res[0]) self.user = res[0].data
        self.company = res[1].data
        fillForms()
        // Update réussi depuis le mode édition → retour au récapitulatif
        // (no-op sur les autres chemins : le flag y est déjà faux).
        self.editingCompany = false
        SnackService.snack({
          text: 'Fiche Pennylane enregistrée',
          delay: 5000
        })
      }).catch(() => {
        // Échec (potentiellement partiel : user créé mais company refusée…) :
        // le toast global affiche l'erreur ; on resynchronise les documents
        // SANS écraser la saisie, pour permettre la correction puis le retry.
        resyncDocuments()
      }).finally(() => {
        self.saving = false
      })
    }

    /** Provisionne la company chez Pennylane (après confirmation). */
    function provision () {
      if (!self.company || self.provisioning) return
      DisplayService.displayConfirm(
        'Provisionner sur Pennylane',
        // Rendu dans le <h4> de confirm.html (ng-bind-html + $sanitize : styles
        // inline supprimés) — <small> pour que l'avertissement passe en texte secondaire.
        'Créer cette entreprise et son dirigeant chez Pennylane ?' +
        '<p><small>Vérifiez la fiche avant de confirmer : le SIREN et l\'email du dirigeant ne seront plus modifiables côté Pennylane.</small></p>'
      ).then(() => {
        self.provisioning = true
        return PennylaneService.provision(self.company._id).then(() => {
          SnackService.snack({
            text: 'Entreprise validée chez Pennylane',
            delay: 5000
          })
        }).catch(() => {
          SnackService.snack({
            text: 'Le provisioning a échoué — statut rechargé',
            delay: 5000
          })
        }).finally(() => {
          self.provisioning = false
          reload()
        })
      }).catch(angular.noop) // fermeture de la modale = annulation
    }

    /**
     * Lance la connexion OAuth Pennylane : récupère l'URL d'autorisation
     * (state anti-CSRF généré côté backend) puis redirige TOUT le navigateur
     * vers Pennylane — le retour se fait sur le state `pennylane-callback`.
     * `connecting` reste vrai après succès : la page est en train de partir,
     * on garde le bouton désactivé (anti double-clic).
     */
    function connect () {
      if (!self.company || self.connecting) return
      self.connecting = true
      PennylaneService.getAuthorizeUrl(self.company._id).then((res) => {
        const url = res && res.data && res.data.authorizeUrl
        if (!url) return $q.reject(new Error('authorizeUrl absente de la réponse'))
        $window.location.href = url
      }).catch(() => {
        // Le toast global (HttpErrorInterceptor) détaille déjà l'erreur serveur.
        self.connecting = false
        SnackService.snack({
          text: 'Impossible de démarrer la connexion Pennylane — réessayez',
          delay: 5000
        })
      })
    }

    /**
     * Déconnecte la company de Pennylane (révocation des tokens côté backend),
     * après confirmation. Le badge connectionStatus est rafraîchi par resync
     * du document company (sans toucher aux formulaires).
     */
    function disconnectPennylane () {
      if (!self.company || self.disconnecting) return
      DisplayService.displayConfirm(
        'Se déconnecter de Pennylane',
        'Révoquer la connexion Pennylane de votre entreprise ? ' +
        'Les échanges de factures électroniques seront suspendus jusqu\'à une nouvelle connexion.'
      ).then(() => {
        self.disconnecting = true
        return PennylaneService.disconnect(self.company._id).then(() => {
          SnackService.snack({
            text: 'Entreprise déconnectée de Pennylane',
            delay: 5000
          })
          // Suivi PA obsolète UNIQUEMENT si la déconnexion a réussi : en cas
          // d'échec la company reste connectée et le tableau reste pertinent.
          self.paRegistrations = null
          self.emissionAllowed = false
          self.paError = false
        }).catch(angular.noop) // échec : le toast global l'affiche déjà
          .finally(() => {
            self.disconnecting = false
            return resyncDocuments()
          })
      }).catch(angular.noop) // fermeture de la modale = annulation
    }

    /**
     * Charge le suivi PA (pa_registrations Pennylane). Tout échec (dont 409
     * « non connectée », toast supprimé par ignoreErrors côté service) →
     * message dégradé, jamais de crash.
     */
    function loadPaRegistrations () {
      if (!self.company || self.paLoading) return
      self.paLoading = true
      self.paError = false
      PennylaneService.getPaRegistrations(self.company._id).then((res) => {
        self.paRegistrations = (res.data && res.data.registrations) || []
        self.emissionAllowed = !!(res.data && res.data.emissionAllowed)
      }).catch(() => {
        self.paError = true
      }).finally(() => {
        self.paLoading = false
      })
    }

    function isProvisioned () {
      return !!self.company && self.company.provisioningStatus === 'provisioned'
    }

    /**
     * Parcours « compte Pennylane existant » (pas de provisioning, OAuth direct) :
     * - fiche créée → la situation PA persistée (pennylane.paSituation) fait foi ;
     * - état A → réponse « Oui » à la question d'aiguillage.
     */
    function isExistingAccountFlow () {
      if (self.company) return !!(self.company.pennylane && self.company.pennylane.paSituation)
      return self.hasExistingAccount === true
    }

    /**
     * Gate du bloc connexion OAuth (remplace l'ancien switch isProvisioned) :
     * company provisionnée (parcours historique) OU parcours « compte
     * existant » avec SIREN ENREGISTRÉ (seul prérequis du backend authorize).
     */
    function canConnect () {
      return isProvisioned() || (isExistingAccountFlow() && hasSavedSiren())
    }

    /**
     * Fiche modifiable après enregistrement UNIQUEMENT sur le parcours
     * « compte existant » (company non provisionnée) — y compris après la
     * connexion OAuth (cas d'usage : SIREN erroné détecté par l'observation
     * reg_no côté backend). Une company provisionnée reste verrouillée
     * comme aujourd'hui (SIREN/dirigeant figés côté Pennylane).
     */
    function canEditCompany () {
      return isExistingAccountFlow() && !isProvisioned()
    }

    /**
     * Bascule le récapitulatif en mode édition : réaffiche le formulaire
     * réduit (bloc Entreprise + situation PA, SIREN obligatoire). Les modèles
     * repartent TOUJOURS de la fiche backend (fillForms) — jamais d'une
     * saisie abandonnée précédemment.
     */
    function startEditingCompany () {
      if (!canEditCompany() || self.saving) return
      fillForms()
      self.editingCompany = true
    }

    /**
     * Quitte le mode édition SANS sauvegarder : restaure les modèles de
     * formulaire depuis la fiche backend (fillForms) puis réaffiche le
     * récapitulatif. Aucun appel réseau.
     */
    function cancelEditingCompany () {
      if (self.saving) return
      fillForms()
      self.editingCompany = false
    }

    /**
     * Affichage des blocs de saisie du formulaire : toujours pour une fiche
     * existante ; en état A, seulement une fois la question d'aiguillage
     * répondue (le parcours historique « Non » reste ensuite inchangé).
     */
    function showForm () {
      return !!self.company || self.hasExistingAccount !== null
    }

    function isConnected () {
      return connectionStatus() === 'connected'
    }

    /** ≥1 enregistrement PA `activated` — sinon on guide vers le consentement Pennylane. */
    function hasActivatedRegistration () {
      return !!self.paRegistrations && self.paRegistrations.some((reg) => reg.status === 'activated')
    }

    /**
     * Situation déclarée « autre Plateforme Agréée désignée » (persistée) :
     * en plus de la désignation de Pennylane, l'utilisateur devra effectuer
     * une demande de portabilité depuis l'interface Pennylane.
     */
    function hasOtherPaSituation () {
      return !!(self.company && self.company.pennylane && self.company.pennylane.paSituation === 'other_pa')
    }

    /**
     * Situation déclarée « Pennylane est déjà ma PA » (persistée) : la
     * désignation est déjà faite côté Pennylane → aucun encart « Action
     * requise », les flux RFE sont directement opérationnels.
     */
    function hasPennylanePaSituation () {
      return !!(self.company && self.company.pennylane && self.company.pennylane.paSituation === 'pennylane')
    }

    /** Lien de désignation/consentement PA côté Pennylane (null si companyId inconnu). */
    function pdpActivationUrl () {
      const pennylaneCompanyId = self.company && self.company.pennylane && self.company.pennylane.companyId
      if (!pennylaneCompanyId) return null
      return 'https://app.pennylane.com/companies/' + pennylaneCompanyId + '/pdp_activation'
    }

    function paStatusLabel (reg) {
      return PA_STATUS_LABELS[reg.status] || reg.status || '—'
    }

    function paDirectionLabel (reg) {
      return PA_DIRECTION_LABELS[reg.exchangeDirection] || reg.exchangeDirection || '—'
    }

    /** SIRET vide = enregistrement au niveau SIREN (siège). */
    function paSiretLabel (reg) {
      return reg.siret || 'Siège (niveau SIREN)'
    }

    /** SIREN réel ENREGISTRÉ côté backend (≠ saisie non sauvegardée) — requis pour provisionner. */
    function hasSavedSiren () {
      return !!self.company && !!self.company.regNo && self.company.regNo !== 'pending'
    }

    function provisioningStatusLabel () {
      const status = self.company && self.company.provisioningStatus
      // Parcours « compte existant » : la fiche n'est volontairement jamais
      // provisionnée → libellé dédié à la place de « Non provisionnée ».
      if (status === 'pending' && isExistingAccountFlow()) return 'Compte Pennylane existant'
      return PROVISIONING_LABELS[status] || status || ''
    }

    /** Libellé FR de la situation PA persistée (brut si valeur inconnue). */
    function paSituationLabel () {
      const value = self.company && self.company.pennylane && self.company.pennylane.paSituation
      const situation = PA_SITUATIONS.find((s) => s.value === value)
      return (situation && situation.label) || value || ''
    }

    function connectionStatus () {
      return (self.company && self.company.pennylane && self.company.pennylane.connectionStatus) || 'disconnected'
    }

    function connectionStatusLabel () {
      return CONNECTION_LABELS[connectionStatus()] || connectionStatus()
    }
  }

  PennylaneSirenDirective.$inject = []

  /**
   * Validateur ngModel `siren` : clé de Luhn sur un SIREN saisi (champ
   * optionnel : vide = valide ; le format 9 chiffres reste couvert par
   * ng-pattern, on ne signale la clé que sur une saisie à 9 chiffres).
   */
  function PennylaneSirenDirective () {
    return {
      restrict: 'A',
      require: 'ngModel',
      link: function (scope, element, attrs, ngModel) {
        ngModel.$validators.siren = function (modelValue, viewValue) {
          const value = modelValue || viewValue
          if (!value || !/^\d{9}$/.test(value)) return true
          return isValidSirenChecksum(value)
        }
      }
    }
  }

  PennylaneSettingsDirective.$inject = []

  /**
   * Onglet de préférences Pennylane (fiche entreprise + dirigeant + provisioning).
   * @type Directive
   * @returns {{restrict: string, templateUrl: string, controller: PennylaneSettingsController, controllerAs: string, scope: {}}}
   * @constructor
   */
  function PennylaneSettingsDirective () {
    return {
      restrict: 'E',
      templateUrl: '/app/common/directives/pennylane-settings/pennylane-settings.directive.html',
      controller: PennylaneSettingsController,
      controllerAs: '$ctrl',
      scope: {}
    }
  }
})(window.angular, window._)
